import { NavigableModal } from '$components'
import { useNavigate, useParams } from 'react-router-dom'
import { EditServiceInfoForm, EditServiceInfoSchema, Input } from '$features/services/forms/edit-service-info/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { serviceShowOptions, serviceShowQueryKey, serviceUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'

export function EditServiceInfoModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...serviceShowOptions({
      path: {
        id: Number(id),
      },
      query: {
        include_deleted: true,
      },
    }),
  })

  const { mutate: updateService, isPending: isUpdatingService } = useMutation({
    ...serviceUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceShowQueryKey({
          path: {
            id: Number(id),
          },
        }),
      })
      ToastHelper.success('Service information updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update service information.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: EditServiceInfoSchema) => {
    updateService({
      path: {
        id: Number(id),
      },
      body: {
        description: values[Input.Description],
        notes: values[Input.Notes],
        currency: values[Input.Currency]?.id,
      },
    })
  }

  const service = data?.data

  if (!service) {
    return null
  }

  return (
    <EditServiceInfoForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingService}
      defaultValues={{
        [Input.Description]: service.values?.description ?? '',
        [Input.Notes]: service.values?.notes ?? '',
        [Input.Currency]: {
          id: service.values!.currency,
          value: service.values!.currency,
        },
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Service Information
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <EditServiceInfoForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <EditServiceInfoForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </EditServiceInfoForm>
  )
}
